import { useDeleteConfirmation, useServices, useToast, getErrorMessage } from '@/app/providers';
import { Loading } from '@/core/ui/components/Loading';
import { useAbility } from '@/modules/identity/application/ability';
import { useI18n } from '@/shared/i18n';
import { AccessLog, Guest, User } from '@/shared/types';
import type { AvailableDevice } from '@yawi/domain';
import { padQRCodeValue } from '@yawi/domain';
import { Loader2, Plus, Unlock, WifiOff } from 'lucide-react';
import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';

// Atomic Components
import { RfidCardSection } from '@/modules/access-control/ui/components/RfidCardSection';
import { Button } from '@/presentation/components/atoms/Button';
import { AccessLogList } from '@/presentation/components/organisms/AccessLogList';
import { GuestForm } from '@/presentation/components/organisms/GuestForm';
import { GuestList } from '@/presentation/components/organisms/GuestList';
import { PinManager } from '@/presentation/components/organisms/PinManager';

export const AccessControl = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { userRepo, guestRepo, accessLogRepo, accessControlRepo } = useServices();
  const ability = useAbility();

  const [activeTab, setActiveTab] = useState<'MY_QR' | 'GUESTS' | 'MY_CARDS' | 'LOGS'>('MY_QR');
  const [pin, setPin] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Data State
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);

  // Available devices for remote open & guest form
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceLoadings, setDeviceLoadings] = useState<Record<string, boolean>>({});

  // Load available devices
  useEffect(() => {
    if (!accessControlRepo) return;
    const fetchDevices = async () => {
      setDevicesLoading(true);
      try {
        const devices = await accessControlRepo.getAvailableDevices();
        setAvailableDevices(devices);
      } catch (error) {
        console.error('Failed to fetch devices', error);
      } finally {
        setDevicesLoading(false);
      }
    };
    fetchDevices();
  }, [accessControlRepo]);

  const handleRemoteOpen = async (deviceId: string) => {
    if (!accessControlRepo || isOffline) return;
    setDeviceLoadings(prev => ({ ...prev, [deviceId]: true }));
    try {
      await accessControlRepo.remoteOpen({ deviceId });
      toast.success('Puerta abierta exitosamente');
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al abrir la puerta');
    } finally {
      setDeviceLoadings(prev => ({ ...prev, [deviceId]: false }));
    }
  };

  // Online status listener
  useEffect(() => {
    const handleStatusChange = () => setIsOffline(!navigator.onLine);
    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);
    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, []);

  // Load User Data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await userRepo.getCurrentUser();
        setCurrentUser(user);
      } catch (error) {
        console.error("Failed to fetch user", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userRepo]);

  // Load Guests when tab changes
  useEffect(() => {
    if (activeTab === 'GUESTS' && currentUser) {
      const fetchGuests = async () => {
        const data = await guestRepo.getGuests(currentUser.id);
        setGuests(data);
      };
      fetchGuests();
    }
  }, [activeTab, currentUser, guestRepo]);

  const lastLogRefreshRef = useRef<number>(0);

  const refreshLogs = useCallback(async () => {
    if (!currentUser) return;
    const now = Date.now();
    if (now - lastLogRefreshRef.current < 3000) return;
    lastLogRefreshRef.current = now;
    try {
      const data = await accessLogRepo.getLogs({ userId: currentUser.id });
      setLogs(data);
    } catch {
      // silent — don't show error on background refresh
    }
  }, [currentUser, accessLogRepo]);

  // Load Logs when tab changes
  useEffect(() => {
    if (activeTab === 'LOGS' && currentUser) {
      refreshLogs();
    }
  }, [activeTab, currentUser, refreshLogs]);

  // Generate QR from PIN whenever PIN changes
  useEffect(() => {
    if (!pin) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(padQRCodeValue(pin), { width: 256, margin: 2 })
      .then(setQrDataUrl)
      .catch((err) => console.error('Error generating QR:', err));
  }, [pin]);

  // Guest Management Handlers
  const handleCreateGuest = async (newGuest: Guest) => {
    setGuests(prev => [newGuest, ...prev]);
    setShowGuestForm(false);
  };

  const handleUpdateGuest = async (updatedGuest: Guest) => {
    setGuests(prev => prev.map(g => g.id === updatedGuest.id ? updatedGuest : g));
    setEditingGuest(null);
  };

  const refreshGuest = async (guestId: string): Promise<Guest | null> => {
    return guestRepo.getGuestById(guestId);
  };

  const handleDeleteGuest = async (id: string, guestName: string) => {
    await confirmDelete({
      itemName: guestName,
      itemType: 'Invitación',
      onConfirm: async () => {
        await guestRepo.deleteGuest(id);
        setGuests(prev => prev.filter(g => g.id !== id));
      }
    });
  };

  if (loading || !currentUser) return <Loading />;

  const canCreateGuest = ability.can('create', 'Guest');
  const canManagePin = ability.can('update', 'AccessControl');

  return (
    <div className="space-y-6">
      {/* Header Molecule */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-slate-800">{t('access.title')}</h2>
        <div className="flex bg-white rounded-lg p-1 shadow-sm border border-slate-200 overflow-x-auto w-full sm:w-auto no-scrollbar">
          {(
            currentUser.tenantRole === 'RESIDENT'
              ? ['MY_QR', 'GUESTS', 'MY_CARDS', 'LOGS']
              : currentUser.tenantRole === 'FAMILIAR'
                ? ['MY_QR', 'GUESTS', 'LOGS']
                : ['MY_QR']
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {t(`access.tab.${tab.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'MY_QR' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in duration-300">

          {/* Organism: QR Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 flex flex-col items-center justify-center text-center relative overflow-hidden">
            {isOffline && (
              <div className="absolute top-0 left-0 w-full bg-amber-100 text-amber-800 text-xs py-1 font-bold flex items-center justify-center gap-1">
                <WifiOff size={12} /> Modo Offline Activo
              </div>
            )}

            <div className="mb-6 mt-2">
              <h3 className="text-xl font-bold text-slate-800 mb-2">{currentUser.name}</h3>
              {currentUser.residenceInfo && (
                <p className="text-slate-500">{currentUser.residenceInfo.tower} - {currentUser.residenceInfo.unit}</p>
              )}
            </div>

            <div className="relative group">
              <div className="bg-white p-4 rounded-xl border-2 border-slate-900 shadow-inner">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt="Access QR"
                    className="w-64 h-64 object-contain"
                  />
                ) : (
                  <div className="w-64 h-64 flex flex-col items-center justify-center text-slate-400 gap-2">
                    <WifiOff size={32} />
                    <span className="text-sm text-center px-4">Genera tu PIN para ver tu código QR</span>
                  </div>
                )}
              </div>
            </div>

            <p className="mt-8 text-sm text-slate-400 max-w-xs">
              Muestre este código en el lector de la entrada principal o vehicular.
            </p>
          </div>

          <div className="space-y-6">
            {/* Organism: Remote Open */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Unlock size={20} className="text-emerald-500" />
                {t('access.remote_open')}
              </h3>
              <p className="text-sm text-slate-500 mb-4">{t('access.remote_warning')}</p>
              {devicesLoading ? (
                <div className="flex items-center justify-center gap-2 py-4 text-slate-400">
                  <Loader2 size={18} className="animate-spin" />
                  <span className="text-sm">Cargando dispositivos...</span>
                </div>
              ) : availableDevices.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No hay dispositivos disponibles</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {availableDevices.map(device => (
                    <Button
                      key={device.id}
                      variant="secondary"
                      className="w-full h-16 border-emerald-100 bg-emerald-50/50 hover:bg-emerald-100 text-emerald-700"
                      onClick={() => handleRemoteOpen(device.id)}
                      disabled={isOffline || deviceLoadings[device.id]}
                      isLoading={deviceLoadings[device.id]}
                    >
                      {device.name}
                    </Button>
                  ))}
                </div>
              )}
              {isOffline && <p className="text-xs text-amber-600 mt-2 text-center">Apertura remota no disponible sin internet.</p>}
            </div>
            
            {/* Organism: Pin Manager */}
            <PinManager onPinChange={setPin} canManagePin={canManagePin} />
          </div>
        </div>
      )}

      {activeTab === 'GUESTS' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {editingGuest ? (
            <GuestForm
              hostId={currentUser.id}
              onCancel={() => setEditingGuest(null)}
              onSuccess={handleUpdateGuest}
              updateGuestFn={(id, data) => guestRepo.updateGuest(id, data)}
              availableDevices={availableDevices}
              initialValues={editingGuest}
            />
          ) : !showGuestForm ? (
            <>
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white shadow-lg flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold mb-1">{t('guest.subtitle')}</h3>
                  <p className="text-slate-400 text-sm">
                    {canCreateGuest
                      ? 'Crea códigos QR temporales o permanentes.'
                      : 'Escanea o verifica los códigos de acceso.'}
                  </p>
                </div>

                {canCreateGuest && (
                  <Button onClick={() => setShowGuestForm(true)} icon={<Plus size={18}/>} className="bg-primary-900 text-slate-900 hover:bg-slate-100 border-none">
                    {t('guest.create_btn')}
                  </Button>
                )}
              </div>

              <GuestList
                guests={guests}
                onDelete={handleDeleteGuest}
                onEdit={canCreateGuest ? setEditingGuest : undefined}
                refreshGuest={refreshGuest}
              />
            </>
          ) : (
            <GuestForm
              hostId={currentUser.id}
              onCancel={() => setShowGuestForm(false)}
              onSuccess={handleCreateGuest}
              createGuestFn={(g) => guestRepo.createGuest(g)}
              availableDevices={availableDevices}
            />
          )}
        </div>
      )}

      {activeTab === 'MY_CARDS' && (
        <RfidCardSection />
      )}

      {activeTab === 'LOGS' && (
        <AccessLogList logs={logs} onRequestRefresh={refreshLogs} />
      )}

    </div>
  );
};
